import { Menu, MenuItem, type MenuProps, type SxProps } from '@mui/material'
import {
  type PropsWithChildren,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import type { SearchResultItem } from '@/features/search/types'

type Props = { item: SearchResultItem } & MenuProps

export const CopyLinkMenu = (props: Props) => {
  const {
    item: { mediaNumber, affiliations },
    ...menuProps
  } = props
  const apiUrl = window.location.origin
  const cwsUrl = `${apiUrl}/n/cwsdirect?media_string_id=`
  const pubDirectUrl = `${apiUrl}/n/api/pubdirect?media_string_id=`

  const menuItems = useMemo(() => {
    const opts = []
    const versionIndex = mediaNumber.lastIndexOf('-')

    const hasVersion = versionIndex === mediaNumber.length - 3
    const latestVersion = hasVersion
      ? mediaNumber.substring(0, mediaNumber.length - 2)
      : null

    const availableOnPubDirect = affiliations.find((a) => a.code === 25)

    opts.push(
      <CopyLinkMenuItem key="CWSDirectLink" url={cwsUrl + mediaNumber}>
        CWSDirect link
      </CopyLinkMenuItem>,
    )
    if (latestVersion)
      opts.push(
        <CopyLinkMenuItem
          key="CWSDirectLinkLatest"
          url={cwsUrl + latestVersion}
        >
          CWSDirect Latest Link
        </CopyLinkMenuItem>,
      )
    if (availableOnPubDirect) {
      opts.push(
        <CopyLinkMenuItem key="PubDirectLink" url={pubDirectUrl + mediaNumber}>
          PubDirect Link
        </CopyLinkMenuItem>,
      )

      if (latestVersion)
        opts.push(
          <CopyLinkMenuItem
            key="PubDirectLinkLatest"
            url={pubDirectUrl + latestVersion}
          >
            PubDirect Latest Link
          </CopyLinkMenuItem>,
        )
    }
    return opts
  }, [mediaNumber, cwsUrl, pubDirectUrl, affiliations])

  return <Menu {...menuProps}>{menuItems}</Menu>
}

type ItemProps = {
  url: string
} & PropsWithChildren

const CopyLinkMenuItem = (props: ItemProps) => {
  const [text, setText] = useState(props.children)
  const [sx, setSx] = useState<SxProps>({})
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current)
    }
  }, [])

  const handleClick = () => {
    setText(`Copied ${props.children}`)
    setSx({
      color: 'success.main',
    })
    navigator.clipboard.writeText(props.url)

    if (timeoutRef.current) clearTimeout(timeoutRef.current)
    timeoutRef.current = setTimeout(() => {
      setText(props.children)
      setSx({})
    }, 1000)
  }
  return (
    <MenuItem key={props.url} sx={sx} onClick={handleClick}>
      {text}
    </MenuItem>
  )
}
